import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { VbaShowcaseContent } from "../data/content";
import VbaConfiguratorShowcase from "./VbaConfiguratorShowcase";
import "./ProjectList.scss";

export type Project = {
  title: string;
  category: string;
  year?: string;
  image?: string;
  secondaryImage?: string;
  url?: string;
  color?: string;
  showcase?: VbaShowcaseContent;
  summary?: string;
  note?: string;
};

type Props = {
  projects: Project[];
  cursorLabel?: string; // napis w kółku, np. "Zobacz"
};

export default function ProjectList({ projects, cursorLabel = "View" }: Props) {
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<HTMLDivElement | null>(null);

  // Podąża za myszką: modal płynniej, kółko trochę szybciej (tylko urządzenia z precyzyjnym wskaźnikiem).
  useEffect(() => {
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!canHover) return;

    const modal = modalRef.current;
    const cursor = cursorRef.current;
    if (!modal || !cursor) return;

    gsap.set([modal, cursor], { xPercent: -50, yPercent: -50, scale: 0 });

    const modalX = gsap.quickTo(modal, "left", { duration: 0.8, ease: "power3" });
    const modalY = gsap.quickTo(modal, "top", { duration: 0.8, ease: "power3" });
    const cursorX = gsap.quickTo(cursor, "left", { duration: 0.5, ease: "power3" });
    const cursorY = gsap.quickTo(cursor, "top", { duration: 0.5, ease: "power3" });

    const onMove = (e: MouseEvent) => {
      modalX(e.clientX);
      modalY(e.clientY);
      cursorX(e.clientX);
      cursorY(e.clientY);
    };

    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Pokazuje i chowa modal oraz kółko.
  useEffect(() => {
    const targets = [modalRef.current, cursorRef.current].filter(Boolean);
    if (!targets.length) return;
    gsap.to(targets, {
      scale: active ? 1 : 0,
      duration: active ? 0.4 : 0.3,
      ease: active ? "power3.out" : "power3.in",
      overwrite: "auto",
    });
  }, [active]);

  const projectHasPreview = (p: Project) => Boolean(p.image || p.secondaryImage);

  const show = (i: number) => {
    if (!projectHasPreview(projects[i])) {
      setActive(false);
      return;
    }
    setIndex(i);
    setActive(true);
  };
  const hide = () => setActive(false);

  const entryHandlers = (i: number) => ({
    onMouseEnter: () => show(i),
    onFocus: () => show(i),
    onBlur: hide,
  });

  const entryContent = (p: Project) => (
    <>
      <span className="project-list__text">
        <span className="project-list__title">{p.title}</span>
        <span className="project-list__meta">
          <span>{p.category}</span>
          {p.year ? <span>{p.year}</span> : null}
        </span>
        {p.summary ? <span className="project-list__summary">{p.summary}</span> : null}
        {p.note ? <span className="project-list__note">{p.note}</span> : null}
      </span>
      {projectHasPreview(p) ? (
        <span className="project-list__thumbs" aria-hidden="true">
          {p.image ? (
            <span className="project-list__thumb">
              <img src={p.image} alt="" loading="lazy" decoding="async" />
            </span>
          ) : null}
          {p.secondaryImage ? (
            <span className="project-list__thumb project-list__thumb--secondary">
              <img src={p.secondaryImage} alt="" loading="lazy" decoding="async" />
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );

  return (
    <section className="project-list" aria-label="Realizacje">
      <ul className="project-list__items" onMouseLeave={hide}>
        {projects.map((p, i) => (
          <li
            key={p.title}
            className={`project-list__item${
              active && index !== i ? " is-dimmed" : ""
            }${!p.url ? " is-static" : ""}${p.showcase ? " project-list__item--showcase" : ""}`}
          >
            {p.url ? (
              <a
                className="project-list__entry"
                href={p.url}
                target="_blank"
                rel="noreferrer"
                {...entryHandlers(i)}
              >
                {entryContent(p)}
              </a>
            ) : (
              <div className="project-list__entry" {...entryHandlers(i)}>
                {entryContent(p)}
              </div>
            )}
            {p.showcase?.kind === "vba-configurator" ? (
              <VbaConfiguratorShowcase config={p.showcase} />
            ) : null}
          </li>
        ))}
      </ul>

      <div className="project-list__modal" ref={modalRef} aria-hidden="true">
        <div
          className="project-list__slider"
          style={{ transform: `translateY(${index * -100}%)` }}
        >
          {projects.map((p) => (
            <div
              className={`project-list__slide${
                p.secondaryImage ? " project-list__slide--duo" : ""
              }`}
              key={p.title}
              style={{ background: p.color ?? "#ebebeb" }}
            >
              {p.image ? (
                <img src={p.image} alt="" loading="lazy" />
              ) : null}
              {p.secondaryImage ? (
                <img src={p.secondaryImage} alt="" loading="lazy" />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="project-list__cursor" ref={cursorRef} aria-hidden="true">
        {cursorLabel}
      </div>
    </section>
  );
}
